import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import CmsBlocks from 'components/CmsBlocks/v2';
import { BOTTOM_MENU_ACTIONS } from 'components/Layout/components/BottomMenu';
import { EFFECT_LIST_BOX_UP_IN_VIEW } from 'constants/effects';
import { FEATURE_REGISTRATION_NEW_CLIENT } from 'constants/featureGates';
import { USER_LEVEL } from 'constants/global';
import { getDigitalLoanStatus } from 'modules/digitalLoan/actions';
import { ROUTE_PATH } from 'routes';
import {
  getPhase3Snapshot,
  savePhase3Snapshot,
} from 'routes/DigitalLoan/utils/phase3Snapshot';
import {
  resolveDigitalLoanEntryStep,
  shouldShowDigitalLoanHomeCard,
} from 'routes/DigitalLoan/utils/phase3Routing';
import { unlockDigitalLoanInternalPreview } from 'utils/digitalLoanPreviewAccess';
import useFeatureGate from 'utils/hooks/useFeatureGate';
import useLiterals from 'utils/hooks/useLiterals';
import { isLegacyDigitalLoanCmsItem } from './utils/legacyDigitalLoanCmsItem';
import UserBox from './components/UserBox';
import ShopBox from './components/ShopBox';
import UserBoxFinishSignup from './components/UserBoxFinishSignup';
import DigitalLoanHomeBanner from './components/DigitalLoanHomeBanner';
import ReferralProgramCard from './components/ReferralProgramCard';
import './styles.scss';

const Home = () => {
  const literals = useLiterals('home');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.session);
  // "Termina tu registro" retoma el onboarding robusto de una cuenta ya creada.
  const canShowRegistration = useFeatureGate(FEATURE_REGISTRATION_NEW_CLIENT);
  const [phase3Status, setPhase3Status] = useState(null);
  const [digitalLoanStatusReady, setDigitalLoanStatusReady] = useState(!user?.id);

  useEffect(() => {
    if (!user?.id) {
      setPhase3Status(null);
      setDigitalLoanStatusReady(true);
      return undefined;
    }
    let cancelled = false;
    setDigitalLoanStatusReady(false);
    dispatch(getDigitalLoanStatus())
      .then((result) => {
        if (!cancelled) setPhase3Status(result);
      })
      .catch(() => {
        if (!cancelled) setPhase3Status(null);
      })
      .finally(() => {
        if (!cancelled) setDigitalLoanStatusReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, user?.id]);

  // Seguimiento desde gestoría. Fase 1 y 2 no pintan este módulo.
  const showTrackingBanner = digitalLoanStatusReady
    && shouldShowDigitalLoanHomeCard(phase3Status);

  const handleStartDigitalLoan = useCallback(() => {
    unlockDigitalLoanInternalPreview();
    navigate(ROUTE_PATH.setDigitalLoan(), {
      state: { digitalLoanIntent: 'new' },
    });
  }, [navigate]);

  const handleViewDigitalLoan = useCallback(() => {
    unlockDigitalLoanInternalPreview();
    const app = phase3Status?.activeApplication;
    const snapshot = getPhase3Snapshot(app?.applicationId);
    const nextStep = resolveDigitalLoanEntryStep({
      currentStep: app?.currentStep,
      currentPhase: app?.currentPhase,
      status: phase3Status,
      snapshot,
      isRobustOnboardingComplete: user?.level === USER_LEVEL.COMPLETE,
    });
    if (app?.applicationId) {
      savePhase3Snapshot({
        applicationId: app.applicationId,
        currentPhase: app.currentPhase || snapshot?.currentPhase || null,
        currentStep: app.currentStep || snapshot?.currentStep || null,
        status: app.status || snapshot?.status || null,
        guaranteeImage: snapshot?.guaranteeImage || null,
        localVisitStatus: snapshot?.localVisitStatus || null,
        paymentDay: snapshot?.paymentDay || '',
      });
    }
    navigate(ROUTE_PATH.setDigitalLoan(nextStep));
  }, [navigate, phase3Status, user?.level]);

  const renderDigitalLoanCmsItem = useCallback((item) => {
    if (!isLegacyDigitalLoanCmsItem(item)) return null;
    return (
      <DigitalLoanHomeBanner
        variant='new'
        literals={literals.digitalLoanBanner}
        onClick={handleStartDigitalLoan}
      />
    );
  }, [handleStartDigitalLoan, literals.digitalLoanBanner]);

  return (
    <Layout
      bottomMenu={{
        type: BOTTOM_MENU_ACTIONS.AUTOMATIC_QUOTER,
      }}
    >
      <div className='home'>
        <CmsBlocks
          animate={EFFECT_LIST_BOX_UP_IN_VIEW}
          renderCmsItem={renderDigitalLoanCmsItem}
        >
          <AppShell variant='default'>
            <UserBox
              user={user}
              literals={literals}
            />
          </AppShell>
          {canShowRegistration && user?.id && user?.level !== USER_LEVEL.COMPLETE && (
            <AppShell variant='default'>
              <UserBoxFinishSignup literals={literals} />
            </AppShell>
          )}
          {showTrackingBanner && (
            <AppShell variant='default'>
              <DigitalLoanHomeBanner
                variant='active'
                literals={literals.digitalLoanBanner}
                onClick={handleViewDigitalLoan}
              />
            </AppShell>
          )}
          <ShopBox />
          {user?.id && (
            <AppShell variant='default'>
              <ReferralProgramCard />
            </AppShell>
          )}
        </CmsBlocks>
      </div>
    </Layout>
  );
};

export default Home;
